import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiFetch } from '../apiClient';
import { ErrorBanner, FIXED_FEE_PROJECT_TYPES } from './Configuration';
import ProjectContactList from './ProjectContactList';
import ChangeRegister from './ChangeRegister';
import RaaiddRegister from './RaaiddRegister';
import BilledAsUsedRegister from './BilledAsUsedRegister';
import ProjectEventsRegister from './ProjectEventsRegister';
import CustomerAccessRequests from './CustomerAccessRequests';
import { TabBar, activeTabKey } from './registerTabs';
import { useSelectedProject, usePruneMissingProject } from './projectDeepLink';
import { useNavGuardConfirm } from '../unsavedChangesGuard';

/**
 * The per-project registers behind one nav item (Jeremy, 2026-09-03). Order is his: contact list,
 * change register, RAAIDD log, billed-as-used, project events, then customer access requests
 * (2026-09-12).
 *
 * `coverage` is each register's "which projects have anything in this?" endpoint. They're per-tab
 * rather than one combined call because the ◇ marker in the picker has to mean "nothing in *this*
 * register yet" -- a single "has anything anywhere" flag would mark a project as covered on a tab
 * that's still empty.
 */
export const REGISTER_TABS = [
  {
    key: 'contacts',
    label: 'Project Contact List',
    coverage: '/project-contacts/projects-with-items',
    Component: ProjectContactList,
    empty: 'Select a project above to see or start its contact list.',
  },
  {
    key: 'change',
    label: 'Change Register',
    coverage: '/change-register-items/projects-with-items',
    Component: ChangeRegister,
    empty: 'Select a project above to see or start its change (CR) register.',
  },
  {
    key: 'raaidd',
    label: 'RAAIDD Register',
    coverage: '/risk-register-items/projects-with-items',
    Component: RaaiddRegister,
    empty: 'Select a project above to see or start its RAAIDD log.',
  },
  {
    key: 'bau',
    label: 'Billed-as-Used Register',
    coverage: '/billed-as-used/projects-with-items',
    Component: BilledAsUsedRegister,
    empty: 'Select a project above to see or start its billed-as-used log.',
  },
  {
    key: 'events',
    label: 'Project Events',
    coverage: '/project-events/projects-with-items',
    Component: ProjectEventsRegister,
    empty: 'Select a project above to see or log its project events.',
  },
  {
    key: 'access-requests',
    label: 'Customer Access Requests',
    coverage: '/customer-access-requests/projects-with-items',
    Component: CustomerAccessRequests,
    empty: 'Select a project above to see or log its customer access requests.',
  },
];

// CustomerProjectRegisters.js derives its own tab list from this one (key/label/Component, no
// coverage calls) so the two pages can't drift on order or labels. A canEdit customer gets real
// editing there (item 5, 2026-09-12), not just a read-only view.

/**
 * Same methodology as ProjectSchedule.js's own `selectableProjects`: drop the OnePlan time-booking
 * buckets, keep archived projects behind a checkbox, and restrict to fixed-fee work -- strictly
 * POC/B&B, as BPATracker does, not the schedule picker's wider POC-or-Internal set (Jeremy,
 * 2026-09-02; B&B added 2026-10-06). A row with no `projectType` predates the column and is treated
 * as the 'POC' default rather than hidden, since hiding on a missing field would silently shrink
 * the list.
 *
 * Lives here rather than in one register's file now that all four share this picker -- ViewAsMenu
 * imports it too, so the fixed-fee rule has one home.
 */
const NON_REGISTER_PROJECT_NAMES = new Set(['Non-Billable Time']);

export function selectableProjects(projects, showArchived) {
  return (projects || []).filter(
    (p) =>
      !NON_REGISTER_PROJECT_NAMES.has(p.name) &&
      FIXED_FEE_PROJECT_TYPES.includes(p.projectType ?? 'POC') &&
      (showArchived || p.projectStatus !== 'Archived'),
  );
}

/**
 * `withItems` is null until the coverage call answers -- marking every project "nothing logged yet"
 * in the meantime would be a visible lie that then flickers away, so nothing is claimed until known.
 */
export function projectOptionLabel(project, withItems) {
  const marker = withItems && !withItems.has(project.id) ? '◇ ' : '';
  const account = project.account?.name ? ` · ${project.account.name}` : '';
  const archived = project.projectStatus === 'Archived' ? ' (archived)' : '';
  return `${marker}${project.name}${account}${archived}`;
}

export default function ProjectRegisters() {
  // Tab in the URL, not component state: a register is the kind of thing people send each other a
  // link to, and the help center can point at one tab directly.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = activeTabKey(searchParams.get('tab'), REGISTER_TABS);

  // Restores whichever project was last selected on any of these pages (ADR 0042), or a
  // `?project=<id>` deep link (ADR 0038) — either way archived projects are shown, so the
  // restored/linked project isn't dropped before it resolves.
  const [projectId, setProjectId] = useSelectedProject();

  const [projects, setProjects] = useState([]);
  // Keyed by tab: { contacts: Set|null, change: Set|null, ... }. Null until that tab's call answers.
  const [coverage, setCoverage] = useState({});
  const [showArchived, setShowArchived] = useState(!!projectId);
  const [error, setError] = useState('');
  const confirmNavigation = useNavGuardConfirm();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // apiFetch returns the PARSED body, not a Response. None of these depend on each other, and
        // a failed coverage call must not cost us the project list -- allSettled, so the picker still
        // works with the ◇ markers simply absent.
        const [projectResult, ...coverageResults] = await Promise.allSettled([
          apiFetch('/projects'),
          ...REGISTER_TABS.map((t) => apiFetch(t.coverage)),
        ]);
        if (cancelled) return;

        if (projectResult.status === 'rejected') throw projectResult.reason;
        const rows = projectResult.value;
        setProjects(rows);
        setCoverage(
          Object.fromEntries(
            REGISTER_TABS.map((t, i) => [
              t.key,
              coverageResults[i].status === 'fulfilled'
                ? new Set(coverageResults[i].value.projectIds)
                : null,
            ]),
          ),
        );
        // A restored/deep-linked selection wins outright; otherwise fall back to auto-selecting the
        // one project there is. `!!projectId` rather than reading `showArchived` state back here --
        // it's the same value at mount (see the state initializer above) and this keeps the effect
        // from needing that state as a dependency for a decision it only ever makes once.
        const initial = selectableProjects(rows, !!projectId);
        if (!projectId && initial.length === 1) setProjectId(initial[0].id);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();
    return () => { cancelled = true; };
    // projectId is read only for its value at mount here (see comment above); depending on it would
    // re-run this on every selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleProjects = useMemo(
    () => selectableProjects(projects, showArchived),
    [projects, showArchived],
  );

  // Unticking "show archived" while an archived project is selected would otherwise leave the grid
  // showing a project the picker no longer lists. Guarded on the list having loaded, or it would
  // clear a deep link before /projects answers — ADR 0038.
  usePruneMissingProject(projectId, setProjectId, visibleProjects, projects.length > 0);

  /**
   * Keeps the ◇ marker honest without a second round trip: each tab reports whether the project it
   * just loaded has anything in it, so logging the first item (or deleting the last) moves the
   * marker straight away. The coverage calls themselves only run on mount. Same approach as
   * ProjectSchedule's `scheduledIds`.
   */
  const noteCoverage = useCallback((tabKey, forProjectId, hasItems) => {
    setCoverage((prev) => {
      const current = prev[tabKey];
      if (!current || current.has(forProjectId) === hasItems) return prev;
      const updated = new Set(current);
      if (hasItems) updated.add(forProjectId);
      else updated.delete(forProjectId);
      return { ...prev, [tabKey]: updated };
    });
  }, []);

  const active = REGISTER_TABS.find((t) => t.key === tab);
  const withItems = coverage[tab] ?? null;
  const ActiveTab = active.Component;

  return (
    <div>
      <div className="page-header">
        <div className="page-eyebrow">Project Management</div>
        <h1 className="page-title">Project Registers</h1>
        <div className="page-subtitle">
          The logs a PM keeps per project — contacts, change requests, the RAAIDD log, the
          billed-as-used drawdown balance, project events, and customer access requests. Each is
          shared with the customer on their own portal.
        </div>
      </div>

      <ErrorBanner error={error} />

      {/* One picker above the tabs, not one per tab: every register is scoped to the same project, so
          switching tabs keeps the project you were already looking at. */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: 280, maxWidth: 520 }}>
            <label className="form-label">Project</label>
            <select
              className="form-select"
              value={projectId}
              onChange={(e) => {
                // A native <select> updates its own displayed selection before onChange fires. On
                // cancel, setProjectId is skipped -- with no re-render, React never resyncs the DOM
                // back to the still-current projectId, leaving the dropdown showing a project whose
                // data isn't actually on screen. Reset it by hand in that one branch.
                if (confirmNavigation()) setProjectId(e.target.value);
                else e.target.value = projectId;
              }}
              disabled={!visibleProjects.length}
            >
              <option value="">
                {visibleProjects.length ? 'Select a project…' : 'No projects yet'}
              </option>
              {visibleProjects.map((p) => (
                <option key={p.id} value={p.id}>{projectOptionLabel(p, withItems)}</option>
              ))}
            </select>
          </div>
          {/* A checkbox rather than another filter: archived projects are the exception you
              occasionally look back at, not a dimension worth its own control. */}
          <label
            style={{
              display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
              color: 'var(--text-mid)', cursor: 'pointer', paddingBottom: 10, whiteSpace: 'nowrap',
            }}
          >
            <input
              type="checkbox"
              checked={showArchived}
              // Can prune the selected project out from under an open tab (usePruneMissingProject,
              // below) the moment it's no longer in `visibleProjects` -- guarded the same as the
              // project picker itself.
              onChange={(e) => {
                if (confirmNavigation()) setShowArchived(e.target.checked);
                else e.target.checked = showArchived;
              }}
            />
            Show archived projects
          </label>
        </div>
        {/* Says why the list is shorter than the project list elsewhere, and explains the marker
            rather than leaving "◇" to be guessed at. The marker note waits for coverage to answer,
            and describes the tab you're on -- a project can have a contact list and no CRs. */}
        <div style={{ fontSize: 12, color: 'var(--text-mid)', marginTop: 8 }}>
          Fixed-fee (POC) projects only.
          {withItems && visibleProjects.some((p) => !withItems.has(p.id)) &&
            ` ◇ marks a project with nothing in the ${active.label} yet.`}
        </div>
      </div>

      <TabBar
        tabs={REGISTER_TABS}
        active={tab}
        // `replace`, so clicking through four tabs doesn't bury the previous page four entries deep
        // in the browser's Back history. Guarded: switching tabs remounts the active one (see the
        // `key` below), which would silently discard an unsaved edit.
        onSelect={(key) => { if (confirmNavigation()) setSearchParams({ tab: key }, { replace: true }); }}
      />

      {!projectId ? (
        <div className="placeholder-page">
          <div className="placeholder-icon">🗂️</div>
          <div className="placeholder-title">Pick a project</div>
          <div className="placeholder-sub">{active.empty}</div>
        </div>
      ) : (
        // Keyed by tab AND project so switching either remounts the tab body -- filters, expanded
        // rows and a half-typed add form from the last project mean nothing here.
        <ActiveTab
          key={`${tab}:${projectId}`}
          projectId={projectId}
          onCoverageChange={(hasItems) => noteCoverage(tab, projectId, hasItems)}
        />
      )}
    </div>
  );
}
